import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Clock, CheckCircle, AlertCircle, User, MapPin, Eye } from 'lucide-react';
import { Task, CreateTaskRequest, Guest } from '../types';
import { taskAPI, guestAPI } from '../services/api';

const TaskManagement: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [detailTask, setDetailTask] = useState<Task | null>(null);
  const [formData, setFormData] = useState<CreateTaskRequest>({
    guest_id: '',
    room_number: '',
    task_description: '',
    priority: 'medium',
    category: 'other',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tasksData, guestsData] = await Promise.all([
        taskAPI.getAllTasks(),
        guestAPI.getAllGuests(),
      ]);
      setTasks(tasksData);
      setGuests(guestsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await taskAPI.createTask(formData);
      await fetchData();
      resetForm();
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const handleStatusChange = async (id: string, status: 'pending' | 'in_progress' | 'completed') => {
    try {
      await taskAPI.updateTaskStatus(id, { status });
      await fetchData();
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await taskAPI.deleteTask(id);
        await fetchData();
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      guest_id: '',
      room_number: '',
      task_description: '',
      priority: 'medium',
      category: 'other',
    });
    setShowModal(false);
  };

  const formatCategory = (c?: string) => {
    if (!c) return '—';
    return c.replace(/_/g, ' ').replace(/\b\w/g, (x) => x.toUpperCase());
  };

  const formatPriority = (p?: string) => {
    if (!p) return '—';
    return p.charAt(0).toUpperCase() + p.slice(1);
  };

  const priorityBadge = (p?: string) => {
    switch (p) {
      case 'urgent':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'low':
        return 'bg-slate-100 text-slate-700';
      default:
        return 'bg-amber-50 text-amber-900';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'in_progress':
        return <AlertCircle className="h-4 w-4" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-grey-guestby">Loading tasks...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-black-guestby">Task Management</h1>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center space-x-2 bg-blue-guestby text-black-guestby px-4 py-2 rounded-lg hover:bg-blue-400 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Add Task</span>
        </button>
      </div>

      {/* Task Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-grey-guestby">Pending Tasks</p>
              <p className="text-2xl font-bold text-yellow-600">
                {tasks.filter(task => task.status === 'pending').length}
              </p>
            </div>
            <Clock className="h-8 w-8 text-yellow-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-grey-guestby">In Progress</p>
              <p className="text-2xl font-bold text-blue-600">
                {tasks.filter(task => task.status === 'in_progress').length}
              </p>
            </div>
            <AlertCircle className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-grey-guestby">Completed</p>
              <p className="text-2xl font-bold text-green-600">
                {tasks.filter(task => task.status === 'completed').length}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* Tasks List */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-black-guestby">All Tasks</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-guestby uppercase tracking-wider min-w-[200px] max-w-md">
                  Task
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-guestby uppercase tracking-wider">
                  Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-guestby uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-guestby uppercase tracking-wider">
                  Guest
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-guestby uppercase tracking-wider">
                  Room
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-guestby uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-guestby uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-guestby uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tasks.map((task) => (
                <tr key={task.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 max-w-md">
                    <div className="text-sm font-medium text-black-guestby line-clamp-2 break-words">
                      {task.task_description}
                    </div>
                    <button
                      type="button"
                      onClick={() => setDetailTask(task)}
                      className="mt-1 inline-flex items-center gap-1 text-xs text-blue-guestby hover:underline"
                    >
                      <Eye className="h-3.5 w-3.5" />
                      View full description
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-black-guestby">
                    {formatCategory(task.category)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${priorityBadge(task.priority)}`}
                    >
                      {formatPriority(task.priority)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-grey-guestby" />
                      <span className="text-sm text-black-guestby">{task.guest_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-grey-guestby" />
                      <span className="text-sm text-black-guestby">{task.room_number}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                        {getStatusIcon(task.status)}
                        <span className="capitalize">{task.status.replace('_', ' ')}</span>
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-grey-guestby">
                    {new Date(task.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <select
                        value={task.status}
                        onChange={(e) => handleStatusChange(task.id, e.target.value as 'pending' | 'in_progress' | 'completed')}
                        className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </select>
                      <button
                        onClick={() => handleDelete(task.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detailTask && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9998] p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg shadow-xl max-h-[85vh] flex flex-col">
            <h2 className="text-lg font-semibold text-black-guestby mb-1">Task details</h2>
            <p className="text-sm text-grey-guestby mb-4">
              {detailTask.guest_name} · Room {detailTask.room_number} ·{' '}
              {formatCategory(detailTask.category)} · {formatPriority(detailTask.priority)}
            </p>
            <div className="text-sm text-black-guestby whitespace-pre-wrap break-words overflow-y-auto flex-1 border border-gray-100 rounded-md p-3 bg-gray-50">
              {detailTask.task_description}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setDetailTask(null)}
                className="px-4 py-2 rounded-lg bg-blue-guestby text-black-guestby hover:bg-blue-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Task Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <h2 className="text-xl font-semibold text-black-guestby mb-4">Add New Task</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-grey-guestby mb-1">
                  Select Guest
                </label>
                <select
                  value={formData.guest_id}
                  onChange={(e) => {
                    const selectedGuest = guests.find(g => g.id === e.target.value);
                    setFormData({
                      ...formData,
                      guest_id: e.target.value,
                      room_number: selectedGuest?.room_number || '',
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                  required
                >
                  <option value="">Select a guest...</option>
                  {guests.filter(guest => guest.status === 'checked_in').map((guest) => (
                    <option key={guest.id} value={guest.id}>
                      {guest.name} - Room {guest.room_number}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-grey-guestby mb-1">
                  Room Number
                </label>
                <input
                  type="text"
                  value={formData.room_number}
                  onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                  placeholder="Auto-filled from guest selection"
                  required
                  readOnly
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-grey-guestby mb-1">
                    Service group
                  </label>
                  <select
                    value={formData.category || 'other'}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        category: e.target.value as CreateTaskRequest['category'],
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                  >
                    <option value="housekeeping">Housekeeping</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="room_service">Room service</option>
                    <option value="concierge">Concierge</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-grey-guestby mb-1">
                    Priority
                  </label>
                  <select
                    value={formData.priority || 'medium'}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        priority: e.target.value as CreateTaskRequest['priority'],
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-grey-guestby mb-1">
                  Task Description
                </label>
                <textarea
                  value={formData.task_description}
                  onChange={(e) => setFormData({ ...formData, task_description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                  rows={3}
                  placeholder="Describe the task to be completed..."
                  required
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-guestby text-black-guestby py-2 px-4 rounded-md hover:bg-blue-400 transition-colors"
                >
                  Add Task
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskManagement;
